import { editUserSchema, userSchema } from '@/model/yupSchema.mjs';
import {
  Box,
  FormControl,
  FormLabel,
  FormErrorMessage,
  FormHelperText,
  Input,
  Select,
  Button
} from '@chakra-ui/react';
import { yupResolver } from '@hookform/resolvers/yup';
import React, { useEffect, useState } from 'react';
import { SubmitHandler, useForm } from 'react-hook-form';
import { useDispatch } from 'react-redux';
import {
    callToEditUser,
  setCreateUserEmailError,
  setIsEditUserDetailsSaved,
  setIsUserCreated
} from '../ducks/slice';
import ModalComponent from '@/common/Modal';
import { EditUserInterface } from '../types';
import {
  getCreateUserEmailError,
  getIsEditUserDetailsSaved,
  getIsSavingEditUserDetails,
  getIsUserCreated
} from '../ducks/selectors';
import * as constants from '../../utils/constants'
import { UserListInterface } from '@/model/userList.mjs';
import { keyType } from '@/queue/types';

export default function EditUserModal({
  isOpen,
  userDetails,
  onClose,
  reFetchData
}: {
  isOpen: boolean;
  onClose: () => void;
  reFetchData(): void;
  userDetails: UserListInterface | null
}) {
  const dispatch = useDispatch();
  const initialFormData = {
    firstName: userDetails ? userDetails.firstName : "",
    lastName: userDetails ? userDetails.lastName : "",
    groupName: userDetails ? constants.USER_ROLES[userDetails.role as keyType<typeof constants.USER_ROLES>] : "",
    accountStatus: userDetails && userDetails?.status ? "enabled" : "disabled",
    phone: userDetails && userDetails.phone ? userDetails.phone.substring(userDetails.phone.length - 10) : "",
  }
  const {
    handleSubmit,
    register,
    formState,
    getValues
  } = useForm({
    resolver: yupResolver(editUserSchema),
    mode: 'onChange',
    values: initialFormData
  });
  const [isSaveBtnEnabled, setIsSaveBtnEnabled] = useState(false);
  const { errors } = formState;

  const onChangeFieldValue = () => {
    const formValues = getValues();
    setIsSaveBtnEnabled(JSON.stringify(initialFormData) !== JSON.stringify(formValues));
  }

  const isSavingEditUserDetails = getIsSavingEditUserDetails();
  const isEditUserDetailsSaved = getIsEditUserDetailsSaved();

  const submitHandler: SubmitHandler<EditUserInterface> = (data) => {
    data.phone = `+1${data.phone}`
    dispatch(callToEditUser({data, userId: userDetails?.email as string}));
  };

  useEffect(() => {
    if (isEditUserDetailsSaved) {
      dispatch(setIsEditUserDetailsSaved(false));
      onClose();
      reFetchData();
    }
  }, [isEditUserDetailsSaved]);

  return (
    <ModalComponent
      title={constants.EDIT_PROFILE}
      isOpen={isOpen}
      onClose={onClose}
      variant={2}
      footer={
        <>
          <Button
            colorScheme="blue"
            background="#ffffff"
            color="#0C1E46"
            border="1px solid #0C1E46"
            padding="8px 11px"
            marginRight="20px"
            onClick={() => {
              onClose();
            }}
          >
            {constants.CANCEL_LABEL}
          </Button>
          <Button
            colorScheme="blue"
            background="#0C1E46"
            padding="8px 20px"
            onClick={handleSubmit(submitHandler)}
            isDisabled={!isSaveBtnEnabled}
            isLoading={isSavingEditUserDetails}
          >
            {constants.SAVE_TEXT}
          </Button>
        </>
      }
    >
      <Box className="user-modal-wrapper">
        <FormControl style={{ width: '300px' }}>
          <FormLabel className="user-form-label">First Name*:</FormLabel>
          <Input
            className="user-from-input"
            type="text"
            placeholder="Enter First Name"
            {...register('firstName', {
                onChange: onChangeFieldValue
            })}
          />
          {errors && errors.firstName ? (
            <div className="form-error">{errors.firstName.message}</div>
          ) : (
            ''
          )}
        </FormControl>
        <FormControl style={{ width: '300px' }}>
          <FormLabel className="user-form-label">Last Name*:</FormLabel>
          <Input
            className="user-from-input"
            type="text"
            placeholder="Enter Last Name"
            {...register('lastName', {
                onChange: onChangeFieldValue
            })}
          />
          {errors && errors.lastName ? (
            <div className="form-error">{errors.lastName.message}</div>
          ) : (
            ''
          )}
        </FormControl>
        <FormControl style={{ width: '300px' }}>
          <FormLabel className="user-form-label">Transfers:</FormLabel>
          <div className="non-edit-value">{userDetails?.transferCount}</div>
        </FormControl>
        <FormControl style={{ width: '300px' }}>
          <FormLabel className="user-form-label">Email:</FormLabel>
          <div className="non-edit-value">{userDetails?.email}</div>
        </FormControl>
        <FormControl style={{ width: '300px' }}>
          <FormLabel className="user-form-label">Role*:</FormLabel>
          <Select
            className="user-from-input"
            placeholder="-Select Role-"
            {...register('groupName', {
                onChange: onChangeFieldValue
            })}
          >
            {
                Object.entries(constants.STORE_ADMIN_ROLE_ACTIONS).map(([value, label]) => <option value={value}>{ label }</option>)
            }
          </Select>
          {errors && errors.groupName ? (
            <div className="form-error">{errors.groupName.message}</div>
          ) : (
            ''
          )}
        </FormControl>
        <FormControl style={{ width: '300px' }}>
          <FormLabel className="user-form-label">Account Status*:</FormLabel>
          <Select
            className="user-from-input"
            placeholder="-Select Status-"
            {...register('accountStatus', {
                onChange: onChangeFieldValue
            })}
          >
            {
                Object.entries(constants.USER_ACCOUNT_STATUS_ACTIONS).map(([value, label]) => <option value={value}>{ label }</option>)
            }
          </Select>
          {errors && errors.accountStatus ? (
            <div className="form-error">{errors.accountStatus.message}</div>
          ) : (
            ''
          )}
        </FormControl>
        <FormControl style={{ width: '300px' }}>
          <FormLabel className="user-form-label">Phone*:</FormLabel>
          <Input
            className="user-from-input"
            type="text"
            maxLength={10}
            placeholder="Enter Phone Number"
            {...register('phone', {
                onChange: onChangeFieldValue
            })}
          />
          {errors && errors.phone ? (
            <div className="form-error">{errors.phone.message}</div>
          ) : (
            ''
          )}
        </FormControl>
      </Box>
    </ModalComponent>
  );
}
